import React, { useState, useEffect, useCallback } from 'react';
import './AlreadyInvoicedPanel.css';
import FinancialService from '../../../../AppSettings/services/FinancialService';
import InvoiceService from '../../../../AppSettings/services/InvoiceService';
import { normalizeToArray } from '../../../../helpers/responseHelpers';
import { buildInvoiceNumberMap, resolveInvoiceNumber } from '../../../../helpers/invoiceNumber';

interface InvoicedItem {
  source_type: string;
  id: string;
  description: string;
  amount: number;
  date: string | null;
  employee: string;
  invoiceid: string;
}

interface InvoiceGroup {
  invoiceid: string;
  displayNumber: string;
  items: InvoicedItem[];
  total: number;
}

interface AlreadyInvoicedPanelProps {
  onClose: () => void;
}

const SOURCE_LABELS: Record<string, string> = {
  billed_time: 'Time',
  fixed_fees: 'Fixed Fee',
  other_billing: 'Other',
};

const SOURCE_TAG_CLASS: Record<string, string> = {
  billed_time: 'tag-time',
  fixed_fees: 'tag-fee',
  other_billing: 'tag-other',
};

const fmt = (n: number) =>
  n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDate = (d: string | null) => {
  if (!d) return '';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return d;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const parseTimeToDisplay = (timeRecorded: string | undefined): string => {
  if (!timeRecorded || String(timeRecorded).trim() === '') return '';
  const s = String(timeRecorded).trim();
  if (s.includes(':')) {
    const parts = s.split(':');
    const h = parseInt(parts[0], 10) || 0;
    const m = parseInt(parts[1], 10) || 0;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  }
  const n = parseFloat(s);
  if (isNaN(n) || n <= 0) return '';
  const h = Math.floor(n);
  const m = Math.round((n - h) * 60);
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

const AlreadyInvoicedPanel: React.FC<AlreadyInvoicedPanelProps> = ({ onClose }) => {
  const [invoiceGroups, setInvoiceGroups] = useState<InvoiceGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedInvoices, setExpandedInvoices] = useState<Set<string>>(new Set());

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [billedTimeData, fixedFeesData, otherBillingData, invoicesData] = await Promise.all([
        FinancialService.getAllBilledTime(),
        FinancialService.getAllAssignedFinancialData(),
        FinancialService.getAllOtherBilling(),
        InvoiceService.getAllInvoices().catch(() => []),
      ]);

      const billedTimeList = normalizeToArray(billedTimeData);
      const fixedFeesList = normalizeToArray(fixedFeesData);
      const otherBillingList = normalizeToArray(otherBillingData);
      const invoiceList = normalizeToArray(invoicesData);
      const invoiceNumberMap = buildInvoiceNumberMap(invoiceList);
      // Authoritative per-invoice header total (includes tax/discount, excludes
      // money-out disbursements). The source line items below don't re-sum to
      // this, so the group/grand totals must come from the invoice header (M14).
      const invoiceTotalMap = new Map<string, number>();
      invoiceList.forEach((inv: any) => {
        if (!inv || inv.invoiceid == null) return;
        const total = parseFloat(inv.total_amount);
        if (Number.isFinite(total)) invoiceTotalMap.set(String(inv.invoiceid), total);
      });

      const allInvoicedItems: InvoicedItem[] = [];

      billedTimeList
        .filter((item: any) => item.invoiceid)
        .forEach((item: any) => {
          const rate = Number(item.billedrate) || 0;
          const amount = Number.isFinite(parseFloat(item.amount)) ? parseFloat(item.amount) : 0;
          const notes = (item.notes || '').trim();
          const duration = parseTimeToDisplay(item.time_recorded);
          const description = notes
            ? `${notes} · $${rate}/hr · ${duration}`
            : `$${rate}/hr · ${duration}`;

          allInvoicedItems.push({
            source_type: 'billed_time',
            id: `bt-${item.bt_id ?? item.id}`,
            description,
            amount,
            date: item.date_recorded ?? item.date ?? null,
            employee: item.employee_name || 'Unknown',
            invoiceid: String(item.invoiceid),
          });
        });

      fixedFeesList.forEach((matter: any) => {
        (matter.fixed_fees || [])
          .filter((fee: any) => fee.invoiceid)
          .forEach((fee: any) => {
            allInvoicedItems.push({
              source_type: 'fixed_fees',
              id: `ff-${fee.assignedid ?? fee.fee_id ?? fee.fee_code}`,
              description: `${fee.fee_code || ''} - ${fee.narrative || ''}`,
              amount: Number.isFinite(parseFloat(fee.fees)) ? parseFloat(fee.fees) : 0,
              date: fee.date_added ?? null,
              employee: 'N/A',
              invoiceid: String(fee.invoiceid),
            });
          });
      });

      otherBillingList
        .filter((item: any) => item.invoiceid)
        .forEach((item: any) => {
          allInvoicedItems.push({
            source_type: 'other_billing',
            id: `ob-${item.ob_id ?? item.id}`,
            description: item.description || '',
            amount: Number.isFinite(parseFloat(item.amount)) ? parseFloat(item.amount) : 0,
            date: item.date ?? null,
            employee: item.employee_name || 'Unknown',
            invoiceid: String(item.invoiceid),
          });
        });

      const groupsMap: Record<string, InvoicedItem[]> = {};
      allInvoicedItems.forEach((item) => {
        if (!groupsMap[item.invoiceid]) {
          groupsMap[item.invoiceid] = [];
        }
        groupsMap[item.invoiceid].push(item);
      });

      const groups: InvoiceGroup[] = Object.entries(groupsMap)
        .map(([invoiceid, items]) => ({
          invoiceid,
          displayNumber: String(resolveInvoiceNumber(invoiceid, invoiceNumberMap) ?? invoiceid),
          items,
          // Prefer the invoice header total; fall back to the item sum only when
          // the invoice record isn't available (M14).
          total: invoiceTotalMap.get(invoiceid) ?? items.reduce((sum, i) => sum + i.amount, 0),
        }))
        .sort((a, b) => b.invoiceid.localeCompare(a.invoiceid));

      setInvoiceGroups(groups);
      if (groups.length <= 5) {
        setExpandedInvoices(new Set(groups.map((g) => g.invoiceid)));
      }
    } catch (err) {
      console.error('Failed to fetch invoiced items:', err);
      setInvoiceGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleInvoice = (invoiceid: string) => {
    setExpandedInvoices((prev) => {
      const next = new Set(prev);
      if (next.has(invoiceid)) next.delete(invoiceid);
      else next.add(invoiceid);
      return next;
    });
  };

  const grandTotal = invoiceGroups.reduce((sum, g) => sum + g.total, 0);
  const totalItems = invoiceGroups.reduce((sum, g) => sum + g.items.length, 0);

  return (
    <>
      <div className="invoiced-overlay" onClick={onClose} />
      <div className="invoiced-panel">
        <div className="invoiced-header">
          <h2>Already Invoiced</h2>
          <button className="invoiced-close-btn" onClick={onClose}>
            &times;
          </button>
        </div>

        {!loading && invoiceGroups.length > 0 && (
          <div className="invoiced-summary-bar">
            <span>
              {totalItems} item{totalItems !== 1 ? 's' : ''} across{' '}
              {invoiceGroups.length} invoice{invoiceGroups.length !== 1 ? 's' : ''}
            </span>
            <span className="invoiced-summary-total">${fmt(grandTotal)}</span>
          </div>
        )}

        <div className="invoiced-content">
          {loading ? (
            <div className="invoiced-loading">
              <div className="invoiced-spinner" />
              <span>Loading...</span>
            </div>
          ) : invoiceGroups.length === 0 ? (
            <div className="invoiced-empty">
              <p>No invoiced items found.</p>
              <p>Billable items will appear here once invoiced.</p>
            </div>
          ) : (
            invoiceGroups.map((group) => {
              const isExpanded = expandedInvoices.has(group.invoiceid);
              return (
                <div key={group.invoiceid} className="invoiced-invoice-group">
                  <div
                    className="invoiced-invoice-header"
                    onClick={() => toggleInvoice(group.invoiceid)}
                  >
                    <div className="invoiced-invoice-left">
                      <span className={`invoiced-chevron ${isExpanded ? 'expanded' : ''}`}>
                        &#9654;
                      </span>
                      <span className="invoiced-invoice-id">Invoice #{group.displayNumber}</span>
                      <span className="invoiced-invoice-count">
                        {group.items.length} item{group.items.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <span className="invoiced-invoice-total">
                      ${fmt(group.total)}
                    </span>
                  </div>

                  {isExpanded && (
                    <div className="invoiced-items-list">
                      {group.items.map((item) => (
                        <div key={item.id} className="invoiced-item-row">
                          <div className="invoiced-item-left">
                            <span className={`invoiced-item-tag ${SOURCE_TAG_CLASS[item.source_type] || ''}`}>
                              {SOURCE_LABELS[item.source_type] || item.source_type}
                            </span>
                            <span className="invoiced-item-desc">{item.description}</span>
                          </div>
                          <div className="invoiced-item-right">
                            <span className="invoiced-item-amount">${fmt(item.amount)}</span>
                            {item.date && (
                              <span className="invoiced-item-date">{formatDate(item.date)}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
};

export default AlreadyInvoicedPanel;
